import { createContext, type JSX, type RefObject, useContext } from 'react'

import type { ShelfProducer } from '@components/storeMap/floor/2d/Floor'
import type { DimType, MapLayer, ZoomLimit } from '@components/storeMap/provider/Provider'
import type { MapOrigin } from '@state/layout/queries'
import type { Shelf } from '@state/shelf/types'

export interface ZoomControls {
  zoomTo: (shelfId: string) => void
  zoomIn: () => void
  zoomOut: () => void
  resetZoom: () => void
}

export interface StoreMapContextType {
  mapOrigin: MapOrigin
  setMapOrigin: (origin: MapOrigin) => void

  showToolbar: boolean

  activeLayers: MapLayer[]
  setActiveLayer: (layer: MapLayer) => void
  unsetActiveLayer: (layer: MapLayer) => void
  isLayerActive: (layer: MapLayer) => boolean
  dimType: DimType
  setDimType: (type: DimType) => void

  selectedShelfId: string | undefined
  setSelectedShelfId: (id: string | undefined) => void

  selectedGtin?: string
  highlightedShelves: string[]

  hoveredShelf?: {element: RefObject<SVGRectElement>; shelf: Shelf}
  setHoveredShelf: (hover: {element: RefObject<SVGRectElement>; shelf: Shelf} | undefined) => void

  overlays2D?: JSX.Element[]
  overlays3D?: JSX.Element[]
  shelfProducer2D?: ShelfProducer

  registerZoomControls: (controls: ZoomControls) => () => void
  zoomToShelf: (shelfId: string) => void
  zoomIn: () => void
  zoomOut: () => void
  resetZoom: () => void

  isTransformed: boolean
  setIsTransformed: (isTransformed: boolean) => void

  zoomAtLimit?: ZoomLimit
  setZoomAtLimit: (reached?: ZoomLimit) => void
}

const StoreMapContext = createContext<StoreMapContextType | undefined>(undefined)

export function useStoreMapContext() {
  const context = useContext(StoreMapContext)

  if (context === undefined) {
    throw new Error('useStoreMapContext must be used within a StoreMapProvider')
  }

  return context
}

export default StoreMapContext